import { variantMime, type ResourceRole, type StoredVariant } from '@os/shared';
import type { FigmaNode } from '@os/doc';
import type { UnfurlResult } from '../features/links/services';
import type { AuthError, PostgrestError } from '@supabase/supabase-js';
import type { Database } from './database.types';
import type { Db } from './supabase';
import type { BoardVersion } from './version-types';
import type { CommentAnchor, CommentMessage, CommentThread } from './comment-types';
import type { AccessEntry, EmailStatus, GrantRole, Invitation, InvitationPeek, SharedItem, ShareLink } from './sharing-types';
import { serviceStatus } from './service-status';
import {
  byPosition,
  ApiError,
  FigmaImportError,
  type Api,
  type ApiErrorCode,
  type AuthUser,
  type Board,
  type Folder,
  type Locale,
  type Project,
  type Quotas,
  type RemoteAsset,
  type TemplateCategory,
  THUMB_SIZE,
} from './api';

type Tables = Database['public']['Tables'];
type BoardRow = Tables['boards']['Row'];
type ProjectRow = Tables['projects']['Row'];
type FolderRow = Tables['folders']['Row'];
type ShareLinkRow = Pick<
  Tables['share_links']['Row'],
  'id' | 'board_id' | 'role' | 'can_export' | 'can_download' | 'protected' | 'requires_account' | 'expires_at' | 'revoked_at' | 'open_count' | 'created_at'
>;

/** Columns a client may read: never `token_hash` (column privileges, P6). */
const LINK_COLUMNS = 'id, board_id, role, can_export, can_download, protected, requires_account, expires_at, revoked_at, open_count, created_at';
const INVITATION_COLUMNS = 'id, email, scope_type, scope_id, org_role, role, expires_at, accepted_at, revoked_at, email_status';

/** SQL exception texts of the sharing RPCs that the UI tells apart. */
const MESSAGES: Record<string, ApiErrorCode> = {
  link_expired: 'expired',
  invitation_expired: 'expired',
  link_invalid: 'not_found',
  invitation_invalid: 'not_found',
  account_required: 'account_required',
  email_mismatch: 'email_mismatch',
  already_member: 'already_member',
};

const PG_CODES: Record<string, ApiErrorCode> = {
  '42501': 'forbidden',
  P0002: 'not_found',
  PGRST116: 'not_found',
  '55000': 'conflict',
  '23505': 'conflict',
  '54000': 'too_large',
  '23503': 'invalid',
  '23514': 'invalid',
  '22023': 'invalid',
  '22P02': 'invalid',
};

/** A restricted Supabase project answers 402; a full database turns read-only (SQLSTATE 25006). */
const isReadOnly = (err: PostgrestError | AuthError | Error, status?: number) =>
  status === 402 || ('status' in err && err.status === 402) || ('code' in err && err.code === '25006') || /read-only transaction/i.test(err.message);

export function toApiError(err: PostgrestError | AuthError | Error, status?: number): ApiError {
  if (isReadOnly(err, status)) {
    serviceStatus.markReadOnly();
    return new ApiError('read_only', err.message);
  }
  if (MESSAGES[err.message]) return new ApiError(MESSAGES[err.message]!, err.message);
  if ('code' in err && typeof err.code === 'string' && PG_CODES[err.code]) return new ApiError(PG_CODES[err.code]!, err.message);
  if ('status' in err && (err.status === 400 || err.status === 401 || err.status === 422) && err.name.startsWith('Auth')) {
    return new ApiError(err.status === 422 ? 'invalid' : 'unauthorized', err.message);
  }
  if (err instanceof TypeError || /fetch|network/i.test(err.message)) return new ApiError('network', err.message);
  return new ApiError('invalid', err.message);
}

function check<T>(res: { data: T; error: PostgrestError | null; status?: number }): NonNullable<T> {
  if (res.error) throw toApiError(res.error, res.status);
  if (res.data === null || res.data === undefined) throw new ApiError('not_found');
  return res.data;
}

// For writes and void RPCs, where data is null on success: one that goes through ends the read-only state (P12).
function run(res: { error: PostgrestError | null; status?: number }): void {
  if (res.error) throw toApiError(res.error, res.status);
  serviceStatus.writeOk();
}

const toProject = (r: ProjectRow): Project => ({
  id: r.id,
  orgId: r.org_id,
  name: r.name,
  clientName: r.client_name,
  createdBy: r.created_by,
  archivedAt: r.archived_at,
  deletedAt: r.deleted_at,
  updatedAt: r.updated_at,
  position: r.position,
  isInbox: r.is_inbox,
});

const toBoard = (r: BoardRow): Board => ({
  id: r.id,
  orgId: r.org_id,
  projectId: r.project_id,
  folderId: r.folder_id,
  title: r.title,
  createdBy: r.created_by,
  archivedAt: r.archived_at,
  deletedAt: r.deleted_at,
  updatedAt: r.updated_at,
  lastEditedAt: r.last_edited_at,
  viewerComments: r.viewer_comments,
  isTemplate: r.is_template,
  templateCategory: (r.template_category as TemplateCategory | null) ?? null,
});

const toFolder = (r: FolderRow): Folder => ({
  id: r.id,
  orgId: r.org_id,
  projectId: r.project_id,
  parentId: r.parent_id,
  name: r.name,
  position: r.position,
});

const toUser = (u: { id: string; email?: string | null; is_anonymous?: boolean } | null | undefined): AuthUser | null =>
  u ? { id: u.id, email: u.email ?? null, isAnonymous: u.is_anonymous ?? false } : null;

const toShareLink = (r: ShareLinkRow): ShareLink => ({
  id: r.id,
  boardId: r.board_id,
  role: r.role as GrantRole,
  canExport: r.can_export,
  canDownload: r.can_download,
  protected: r.protected,
  requiresAccount: r.requires_account ?? r.role === 'editor',
  expiresAt: r.expires_at,
  revokedAt: r.revoked_at,
  openCount: r.open_count,
  createdAt: r.created_at,
});

const captcha = (token: string | undefined) => (token ? { captchaToken: token } : {});

const LIFECYCLE_RPC = {
  board: {
    archive: 'archive_board',
    unarchive: 'unarchive_board',
    trash: 'trash_board',
    restore: 'restore_board',
    purge: 'purge_board',
  },
  project: {
    archive: 'archive_project',
    unarchive: 'unarchive_project',
    trash: 'trash_project',
    restore: 'restore_project',
    purge: 'purge_project',
  },
} as const;

/** `fetch` is injectable for tests; the Worker is on the same origin as the SPA. */
export function createSupabaseApi(db: Db, opts: { fetch?: typeof fetch } = {}): Api {
  /** Names and colours of the given users, as far as RLS lets the caller see their profiles. */
  const profilesOf = async (ids: string[]): Promise<Map<string, { name: string; color: string | null }>> => {
    const unique = [...new Set(ids)];
    if (!unique.length) return new Map();
    const rows = check(await db.from('profiles').select('id, display_name, color').in('id', unique));
    return new Map(rows.map((p) => [p.id, { name: p.display_name, color: p.color }]));
  };
  const fetchImpl = opts.fetch ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
  const uid = async (): Promise<string> => {
    const { data } = await db.auth.getSession();
    const id = data.session?.user.id;
    if (!id) throw new ApiError('unauthorized');
    return id;
  };
  const accessToken = async (): Promise<string> => {
    const { data } = await db.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new ApiError('unauthorized');
    return token;
  };
  const toWorker = async (url: string, init: RequestInit): Promise<Response> => {
    try {
      return await fetchImpl(url, init);
    } catch {
      throw new ApiError('network');
    }
  };
  /** Upload answers from the Worker (routes/assets.ts); a 404 that is not the Worker's own is a network problem. */
  const uploadFailure = async (res: Response): Promise<void> => {
    if (res.ok) return;
    const error = ((await res.json().catch(() => null)) as { error?: string } | null)?.error;
    if (res.status === 413) throw new ApiError(error === 'quota' ? 'quota' : 'too_large');
    if (res.status === 404) throw new ApiError(error === 'not_found' ? 'not_found' : 'network');
    if (res.status === 401) throw new ApiError('unauthorized');
    if (res.status === 400 || res.status === 411 || res.status === 415) throw new ApiError('invalid', error);
    throw new ApiError('network', `upload: HTTP ${res.status}`);
  };
  /** Avatar answers from the Worker (routes/me.ts): a guest or non-member is refused, ten photos an hour at most. */
  const avatarFailure = async (res: Response): Promise<void> => {
    if (res.status === 403) throw new ApiError('forbidden');
    if (res.status === 429) throw new ApiError('limit');
    await uploadFailure(res);
  };
  /** Version answers from the Worker (routes/versions.ts); as for tickets, only the Worker's own 404 means not found. */
  const versionFailure = async (res: Response): Promise<void> => {
    if (res.ok) return;
    const error = ((await res.json().catch(() => null)) as { error?: string } | null)?.error;
    if (res.status === 404) throw new ApiError(error === 'not_found' ? 'not_found' : 'network');
    if (res.status === 401) throw new ApiError('unauthorized');
    if (res.status === 409) throw new ApiError(error === 'limit' ? 'limit' : 'conflict');
    if (res.status === 400 || res.status === 422) throw new ApiError('invalid', error);
    throw new ApiError('network', `versions: HTTP ${res.status}`);
  };
  /**
   * Answers of the Worker's invitation and account routes (P17): its own `error` body, by status, decides; a refusal
   * without it (no Worker in front, a gateway) or an upstream failure (502 unavailable, 503) is a network problem, never
   * an invalid invitation or a wrong password.
   */
  const workerFailure = async (res: Response, label: string, codes: Partial<Record<number, Record<string, ApiErrorCode>>>): Promise<ApiError> => {
    if (res.status === 429) return new ApiError('limit'); // { error: 'rate' } + Retry-After
    if (res.status === 413) return new ApiError('too_large');
    const error = ((await res.json().catch(() => null)) as { error?: string } | null)?.error;
    const code = error ? codes[res.status]?.[error] : undefined;
    return code ? new ApiError(code) : new ApiError('network', `${label}: HTTP ${res.status}`);
  };
  const workerJson = async <T>(res: Response, label: string): Promise<T> => {
    try {
      return (await res.json()) as T;
    } catch {
      throw new ApiError('network', `${label}: not JSON`);
    }
  };
  const authCheck = (res: { error: AuthError | null }) => {
    if (res.error) throw toApiError(res.error);
  };

  return {
    capabilities: { sharing: true, comments: true, versions: true, boardCopy: true, avatars: true, figma: true },
    auth: {
      async getUser() {
        const { data } = await db.auth.getSession();
        return toUser(data.session?.user);
      },
      onChange(listener) {
        const { data } = db.auth.onAuthStateChange((_event, session) => listener(toUser(session?.user)));
        return () => data.subscription.unsubscribe();
      },
      async signInWithPassword(email, password, captchaToken) {
        authCheck(await db.auth.signInWithPassword({ email, password, options: captcha(captchaToken) }));
      },
      async signInWithOtp(email, redirectTo, captchaToken) {
        authCheck(
          await db.auth.signInWithOtp({ email, options: { emailRedirectTo: redirectTo, shouldCreateUser: false, ...captcha(captchaToken) } }),
        );
      },
      async signInWithGoogle(redirectTo) {
        authCheck(await db.auth.signInWithOAuth({ provider: 'google', options: { redirectTo } }));
      },
      async exchangeCode(code) {
        authCheck(await db.auth.exchangeCodeForSession(code));
      },
      async resetPassword(email, redirectTo, captchaToken) {
        authCheck(await db.auth.resetPasswordForEmail(email, { redirectTo, ...captcha(captchaToken) }));
      },
      async signInAnonymously(captchaToken) {
        authCheck(await db.auth.signInAnonymously({ options: captcha(captchaToken) }));
      },
      async updatePassword(password) {
        authCheck(await db.auth.updateUser({ password }));
      },
      async signOut() {
        authCheck(await db.auth.signOut());
      },
    },

    async myOrgs() {
      const me = await uid();
      const rows = check(
        await db
          .from('org_members')
          .select('role, organizations(id, name, slug)')
          .eq('user_id', me)
          .order('created_at'), // deterministic "first organization" (useOrg)
      );
      return rows.flatMap((r) => (r.organizations ? [{ ...r.organizations, role: r.role }] : []));
    },

    async listProjects(orgId, opts) {
      // P17: « Sans projet » is a dashboard section (`orgInbox`), never a project of the lists.
      let q = db.from('projects').select('*').eq('org_id', orgId).eq('is_inbox', false);
      q = opts?.trashed ? q.not('deleted_at', 'is', null) : q.is('deleted_at', null);
      return check(await q.order('position').order('updated_at', { ascending: false })).map(toProject);
    },
    async orgInbox(orgId) {
      return check(await db.rpc('org_inbox', { p_org: orgId }));
    },
    async reorderProjects(orgId, positions) {
      return check(
        await db.rpc('project_reorder', { p_org: orgId, p_ids: positions.map((p) => p.id), p_positions: positions.map((p) => p.position) }),
      );
    },
    async getProject(id) {
      return toProject(check(await db.from('projects').select('*').eq('id', id).single()));
    },
    async createProject({ orgId, name, clientName }) {
      const row = check(
        await db.from('projects').insert({ org_id: orgId, name, client_name: clientName ?? null }).select('*').single(),
      );
      return toProject(row);
    },
    async updateProject(id, patch) {
      run(
        await db
          .from('projects')
          .update({
            ...(patch.name !== undefined && { name: patch.name }),
            ...(patch.clientName !== undefined && { client_name: patch.clientName }),
          })
          .eq('id', id),
      );
    },

    async listFolders(projectId) {
      // Sorted here: the column's locale collation would not order fractional keys.
      return check(await db.from('folders').select('*').eq('project_id', projectId)).map(toFolder).sort(byPosition);
    },
    async createFolder({ orgId, projectId, name }) {
      return toFolder(check(await db.from('folders').insert({ org_id: orgId, project_id: projectId, name }).select('*').single()));
    },
    async renameFolder(id, name) {
      run(await db.from('folders').update({ name }).eq('id', id));
    },
    async setFolderPosition(id, position) {
      run(await db.from('folders').update({ position }).eq('id', id));
    },
    async deleteFolder(id) {
      run(await db.from('folders').delete().eq('id', id));
    },

    async listBoards({ orgId, projectId, trashed, templates }) {
      let q = db.from('boards').select('*').eq('org_id', orgId);
      if (projectId) q = q.eq('project_id', projectId);
      q = trashed ? q.not('deleted_at', 'is', null) : q.is('deleted_at', null).eq('is_template', Boolean(templates));
      return check(await q.order('updated_at', { ascending: false })).map(toBoard);
    },
    async listTemplates(orgId) {
      const q = db.from('boards').select('*, projects!inner(deleted_at)').eq('org_id', orgId).eq('is_template', true).is('deleted_at', null).is('projects.deleted_at', null);
      return check(await q.order('updated_at', { ascending: false })).map((r) => toBoard(r as BoardRow));
    },
    async getBoard(id) {
      const [r] = check(await db.rpc('board_access', { p_board: id }));
      if (!r) throw new ApiError('not_found');
      return {
        board: {
          id: r.board_id,
          orgId: r.org_id,
          projectId: r.project_id,
          folderId: r.folder_id,
          title: r.title,
          createdBy: r.created_by,
          archivedAt: r.archived_at,
          deletedAt: r.deleted_at,
          updatedAt: r.updated_at,
          lastEditedAt: r.last_edited_at,
          viewerComments: r.viewer_comments,
          isTemplate: r.is_template,
          templateCategory: (r.template_category as TemplateCategory | null) ?? null,
        },
        project: { id: r.project_id, name: r.project_name, archivedAt: r.project_archived_at, deletedAt: r.project_deleted_at },
        role: r.role,
        flags: { canExport: r.can_export, canDownload: r.can_download, protected: r.protected },
        isMember: r.is_member,
        linkOnly: r.link_only,
      };
    },
    async createBoard({ orgId, projectId, folderId, title }) {
      const row = check(
        await db
          .from('boards')
          .insert({ org_id: orgId, project_id: projectId, folder_id: folderId ?? null, title })
          .select('*')
          .single(),
      );
      return toBoard(row);
    },
    async updateBoard(id, patch) {
      run(
        await db
          .from('boards')
          .update({
            ...(patch.title !== undefined && { title: patch.title }),
            ...(patch.projectId !== undefined && { project_id: patch.projectId }),
            ...(patch.folderId !== undefined && { folder_id: patch.folderId }),
            ...(patch.viewerComments !== undefined && { viewer_comments: patch.viewerComments }),
          })
          .eq('id', id),
      );
    },
    async lifecycle(kind, action, id) {
      const fn = LIFECYCLE_RPC[kind][action];
      const args = kind === 'board' ? { p_board: id } : { p_project: id };
      run(await db.rpc(fn, args as never));
    },

    async listRecents(limit = 20) {
      const rows = check(
        await db.from('board_views').select('viewed_at, boards(*)').order('viewed_at', { ascending: false }).limit(limit),
      );
      return rows.flatMap((r) => (r.boards && !r.boards.deleted_at ? [{ board: toBoard(r.boards), viewedAt: r.viewed_at }] : []));
    },
    async listFavorites() {
      const rows = check(await db.from('favorites').select('boards(*)').order('created_at', { ascending: false }));
      return rows.flatMap((r) => (r.boards && !r.boards.deleted_at ? [toBoard(r.boards)] : []));
    },
    async setFavorite(boardId, on) {
      if (on) {
        const res = await db.from('favorites').insert({ board_id: boardId });
        if (res.error && res.error.code !== '23505') throw toApiError(res.error, res.status);
      } else {
        run(await db.from('favorites').delete().eq('board_id', boardId));
      }
    },
    async touchBoardView(boardId) {
      run(await db.rpc('touch_board_view', { p_board: boardId }));
    },
    async searchTitles(q) {
      if (!q.trim()) return [];
      return check(await db.rpc('search_titles', { q })).map((h) => ({
        kind: h.kind as 'project' | 'board' | 'board_text',
        id: h.id,
        title: h.title,
        projectId: h.project_id,
        projectName: h.project_name,
      }));
    },

    async getProfile() {
      const me = await uid();
      const r = check(await db.from('profiles').select('*').eq('id', me).single());
      return {
        id: r.id, displayName: r.display_name, avatarKey: r.avatar_key, locale: r.locale as Locale, color: r.color, commentDigest: r.comment_digest,
      };
    },
    async updateProfile(patch) {
      const me = await uid();
      run(
        await db
          .from('profiles')
          .update({
            ...(patch.displayName !== undefined && { display_name: patch.displayName }),
            ...(patch.locale !== undefined && { locale: patch.locale }),
            ...(patch.color !== undefined && { color: patch.color }),
            ...(patch.commentDigest !== undefined && { comment_digest: patch.commentDigest }),
          })
          .eq('id', me),
      );
    },
    async listMembers(orgId) {
      const members = check(await db.from('org_members').select('user_id, role').eq('org_id', orgId));
      const ids = members.map((m) => m.user_id);
      const profiles = ids.length ? check(await db.from('profiles').select('id, display_name').in('id', ids)) : [];
      const names = new Map(profiles.map((p) => [p.id, p.display_name]));
      return members.map((m) => ({ userId: m.user_id, role: m.role, displayName: names.get(m.user_id) ?? '' }));
    },
    async addMember(orgId, email, role) {
      run(await db.rpc('add_org_member', { p_org: orgId, p_email: email, p_role: role }));
    },
    async removeMember(orgId, userId) {
      run(await db.rpc('remove_org_member', { p_org: orgId, p_user: userId }));
    },

    async getRoomTicket(boardId) {
      const token = await accessToken();
      let res: Response;
      try {
        res = await fetchImpl(`/api/rooms/${encodeURIComponent(boardId)}/ticket`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        throw new ApiError('network');
      }
      if (res.status === 404) {
        // Only the Worker's own answer means "no access"; any other 404 means the Worker is not there.
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new ApiError(body?.error === 'not_found' ? 'not_found' : 'network');
      }
      if (res.status === 401) throw new ApiError('unauthorized');
      if (!res.ok) throw new ApiError('network', `room ticket: HTTP ${res.status}`);
      const body = (await res.json()) as { ticket: string; role: ResourceRole };
      return { ticket: body.ticket, role: body.role };
    },

    async checkAsset(boardId, sha256) {
      const res = await db.rpc('asset_check', { p_board: boardId, p_sha256: sha256 });
      if (res.error) throw toApiError(res.error, res.status);
      return res.data ?? null;
    },
    async assetUrls(boardId, assetIds) {
      if (assetIds.length === 0) return [];
      const rows = check(await db.rpc('asset_urls', { p_board: boardId, p_ids: assetIds }));
      const byId = new Map<string, RemoteAsset>();
      for (const r of rows) {
        const a = byId.get(r.asset_id) ?? {
          assetId: r.asset_id, mime: r.mime, width: r.width, height: r.height, thumbhash: r.thumbhash, status: r.status, keys: {},
        };
        a.keys[r.variant as StoredVariant] = r.key;
        byId.set(r.asset_id, a);
      }
      return [...byId.values()];
    },
    async putAssetVariant({ boardId, assetId, variant, blob, meta }) {
      const res = await toWorker(`/api/assets/${encodeURIComponent(assetId)}/${variant}?board=${encodeURIComponent(boardId)}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${await accessToken()}`, 'Content-Type': variantMime(variant), 'X-Asset-Meta': JSON.stringify(meta) },
        body: blob,
      });
      if (res.status === 409) return { duplicateOf: ((await res.json()) as { duplicateOf: string }).duplicateOf };
      await uploadFailure(res);
      return { status: ((await res.json()) as { status: 'uploading' | 'ready' }).status };
    },
    async setAvatar(webp) {
      // A Blob body: the browser sets Content-Length itself (the route needs it, scripts may not set it).
      const res = await toWorker('/api/me/avatar', { method: 'PUT', headers: { Authorization: `Bearer ${await accessToken()}`, 'Content-Type': 'image/webp' }, body: webp });
      await avatarFailure(res);
      return ((await res.json()) as { avatarKey: string }).avatarKey;
    },
    async removeAvatar() {
      await avatarFailure(await toWorker('/api/me/avatar', { method: 'DELETE', headers: { Authorization: `Bearer ${await accessToken()}` } }));
    },
    async putBoardThumbnail(boardId, blob) {
      const res = await toWorker(`/api/boards/${encodeURIComponent(boardId)}/thumbnail`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${await accessToken()}`, 'Content-Type': 'image/webp', 'X-Thumb-Size': `${THUMB_SIZE.w}x${THUMB_SIZE.h}` },
        body: blob,
      });
      await uploadFailure(res);
    },
    async unfurl(url) {
      const res = await toWorker(`/api/unfurl?url=${encodeURIComponent(url)}`, { headers: { Authorization: `Bearer ${await accessToken()}` } });
      if (res.status === 401) throw new ApiError('unauthorized');
      if (res.status === 400) throw new ApiError('invalid');
      if (!res.ok) throw new ApiError('network');
      return (await res.json()) as UnfurlResult;
    },
    async figmaFile(input, signal) {
      let res: Response;
      try {
        res = await toWorker('/api/import/figma', { method: 'POST', signal, headers: { Authorization: `Bearer ${await accessToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(input) });
      } catch (err) {
        if (signal?.aborted) throw err;
        throw new FigmaImportError('network');
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; retryAfter?: number };
        const known = ['figma_token', 'figma_private', 'figma_not_found', 'figma_rate', 'figma_too_large', 'figma_upstream', 'forbidden', 'rate'] as const;
        const code = known.find((k) => k === body.error) ?? (res.status === 400 ? 'invalid' : 'network');
        throw new FigmaImportError(code, typeof body.retryAfter === 'number' ? body.retryAfter : null);
      }
      try {
        const { images, file } = (await res.json()) as { images?: { meta?: { images?: Record<string, string | null> } }; file?: { name?: string; document?: FigmaNode } };
        const urls = Object.fromEntries(Object.entries(images?.meta?.images ?? {}).filter((e): e is [string, string] => typeof e[1] === 'string'));
        if (!file?.document) throw new Error('shape');
        return { name: typeof file.name === 'string' ? file.name : '', document: file.document, images: urls };
      } catch (err) {
        if (signal?.aborted) throw err;
        throw new FigmaImportError('figma_too_large'); // a body cut by the Worker's size limit, or not a Figma file
      }
    },
    async figmaImage(boardId, url, signal) {
      try {
        const res = await toWorker('/api/import/figma/image', { method: 'POST', signal, headers: { Authorization: `Bearer ${await accessToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ boardId, url }) });
        return res.ok ? await res.blob() : null;
      } catch (err) {
        if (signal?.aborted) throw err;
        return null;
      }
    },
    async unfurlImage(url) {
      const res = await toWorker(`/api/unfurl/image?url=${encodeURIComponent(url)}`, { headers: { Authorization: `Bearer ${await accessToken()}` } });
      if (!res.ok) throw new ApiError(res.status === 401 ? 'unauthorized' : 'network');
      return res.blob();
    },
    async boardThumbnails(boardIds) {
      if (boardIds.length === 0) return new Map();
      const rows = check(await db.rpc('board_thumbnails', { p_ids: boardIds }));
      return new Map(rows.map((r) => [r.board_id, r.key]));
    },
    async orgStorage(orgId) {
      const [row] = check(await db.rpc('org_storage', { p_org: orgId }));
      if (!row) throw new ApiError('not_found');
      return { usedBytes: row.used_bytes, quotaBytes: row.quota_bytes };
    },
    async lightenPreview(orgId, days, versions) {
      const [r] = check(await db.rpc('org_lighten_preview', { p_org: orgId, p_days: days, p_versions: versions }));
      return { versionCount: r?.version_count ?? 0, versionBytes: r?.version_bytes ?? 0, orphanCount: r?.orphan_count ?? 0, orphanBytes: r?.orphan_bytes ?? 0 };
    },
    async lighten(orgId, { days, versions, images }) {
      const [r] = check(await db.rpc('org_lighten', { p_org: orgId, p_days: days, p_versions: versions, p_images: images }));
      return { versionCount: r?.version_count ?? 0, orphanCount: r?.orphan_count ?? 0 };
    },
    async getQuotas(orgId) {
      const res = await toWorker(`/api/admin/quotas?org=${encodeURIComponent(orgId)}`, { headers: { Authorization: `Bearer ${await accessToken()}` } });
      if (res.status === 401) throw new ApiError('unauthorized');
      if (res.status === 403) throw new ApiError('forbidden');
      if (!res.ok) throw new ApiError('network');
      return (await res.json()) as Quotas;
    },
    async storageReport(orgId) {
      return check(await db.rpc('org_storage_report', { p_org: orgId })).map((r) => ({
        boardId: r.board_id,
        title: r.title,
        projectId: r.project_id,
        projectName: r.project_name,
        ownerId: r.owner_id,
        ownerName: r.owner_name || null,
        docBytes: r.doc_bytes,
        imageBytes: r.image_bytes,
        imageCount: r.image_count,
        sharedImageCount: r.shared_image_count,
        versionBytes: r.version_bytes,
        versionCount: r.version_count,
        objectCount: r.object_count,
        lastEditedAt: r.last_edited_at,
        archivedAt: r.archived_at,
        deletedAt: r.deleted_at,
      }));
    },

    async createShareLink({ boardId, role, expires, canExport, canDownload, protected: isProtected }) {
      const [made] = check(
        await db.rpc('create_share_link', {
          p_board: boardId, p_role: role, p_expires: expires, p_can_export: canExport, p_can_download: canDownload, p_protected: isProtected,
        }),
      );
      if (!made) throw new ApiError('not_found');
      const link = check(await db.from('share_links').select(LINK_COLUMNS).eq('id', made.id).single()) as ShareLinkRow;
      return { link: toShareLink(link), token: made.token };
    },
    async listShareLinks(boardId) {
      const rows = check(await db.from('share_links').select(LINK_COLUMNS).eq('board_id', boardId).order('created_at', { ascending: false }));
      return (rows as ShareLinkRow[]).map(toShareLink);
    },
    async revokeShareLink(id) {
      run(await db.rpc('revoke_share_link', { p_link: id }));
    },
    async redeemShareLink(token) {
      const [r] = check(await db.rpc('redeem_share_link', { p_token: token }));
      if (!r) throw new ApiError('not_found');
      return { boardId: r.board_id, role: r.role };
    },
    async listAccess(scope) {
      const rows = check(await db.rpc('list_access', { p_scope_type: scope.type, p_scope_id: scope.id }));
      return rows.map(
        (r): AccessEntry => ({
          kind: r.kind as AccessEntry['kind'],
          userId: r.user_id,
          displayName: r.display_name,
          role: r.role as GrantRole,
          canExport: r.can_export,
          canDownload: r.can_download,
          expiresAt: r.expires_at,
          linkId: r.link_id,
        }),
      );
    },
    async setGrant(scope, userId, grant) {
      run(
        await db.rpc('set_grant', {
          p_scope_type: scope.type, p_scope_id: scope.id, p_user: userId, p_role: grant.role,
          p_can_export: grant.canExport, p_can_download: grant.canDownload, p_expires_at: grant.expiresAt as string,
        }),
      );
    },
    async removeGrant(scope, userId) {
      run(await db.rpc('remove_grant', { p_scope_type: scope.type, p_scope_id: scope.id, p_user: userId }));
    },
    async invite({ scope, email, role, canExport = false, canDownload = false, expires, locale }) {
      const res = await toWorker('/api/invitations', {
        method: 'POST',
        headers: { Authorization: `Bearer ${await accessToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ scopeType: scope.type, scopeId: scope.id, email, role, canExport, canDownload, expires, locale }),
      });
      if (res.status === 201) return (await res.json()) as { id: string; link: string; emailStatus: EmailStatus };
      const error = ((await res.json().catch(() => null)) as { error?: string } | null)?.error;
      if (res.status === 401) throw new ApiError('unauthorized');
      if (res.status === 400) throw new ApiError('invalid');
      if (res.status === 403) throw new ApiError('forbidden');
      if (res.status === 409) throw new ApiError('already_member');
      if (res.status === 404 && error === 'not_found') throw new ApiError('not_found');
      throw new ApiError('network', `invite: HTTP ${res.status}`);
    },
    async listInvitations(scope) {
      const rows = check(
        await db
          .from('invitations')
          .select(INVITATION_COLUMNS)
          .eq('scope_type', scope.type)
          .eq('scope_id', scope.id)
          .order('created_at', { ascending: false }),
      ) as Array<{
        id: string; email: string; scope_type: string; scope_id: string; org_role: string | null; role: string | null;
        expires_at: string; accepted_at: string | null; revoked_at: string | null; email_status: string;
      }>;
      return rows.map(
        (r): Invitation => ({
          id: r.id,
          email: r.email,
          scope: { type: r.scope_type as Invitation['scope']['type'], id: r.scope_id },
          role: r.org_role ?? r.role ?? '',
          expiresAt: r.expires_at,
          acceptedAt: r.accepted_at,
          revokedAt: r.revoked_at,
          emailStatus: r.email_status as EmailStatus,
        }),
      );
    },
    async revokeInvitation(id) {
      run(await db.rpc('revoke_invitation', { p_id: id }));
    },
    async peekInvitation(token) {
      // No credentials: the person has no account yet. The token travels in the body, never in the URL.
      const res = await toWorker('/api/invitations/peek', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }) });
      if (!res.ok) throw await workerFailure(res, 'peek', { 400: { invalid: 'invalid' }, 404: { invitation_invalid: 'invitation_invalid' }, 410: { invitation_expired: 'invitation_expired' } });
      return workerJson<InvitationPeek>(res, 'peek');
    },
    async signupWithInvitation(token, { password, displayName, locale }) {
      const res = await toWorker('/api/invitations/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, displayName, locale }),
      });
      if (!res.ok) {
        throw await workerFailure(res, 'signup', {
          400: { invalid: 'invalid' },
          404: { invitation_invalid: 'invitation_invalid' },
          409: { account_exists: 'account_exists' },
          410: { invitation_expired: 'invitation_expired' },
        });
      }
      return { email: (await workerJson<{ email: string }>(res, 'signup')).email };
    },
    async changeEmail({ email, password, captchaToken }) {
      const res = await toWorker('/api/me/email', {
        method: 'POST',
        headers: { Authorization: `Bearer ${await accessToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, ...captcha(captchaToken) }),
      });
      if (!res.ok) {
        throw await workerFailure(res, 'change email', {
          400: { invalid: 'invalid', captcha_failed: 'captcha_failed' },
          401: { unauthorized: 'unauthorized', invalid_credentials: 'invalid_credentials' },
          403: { forbidden: 'forbidden' },
          409: { email_taken: 'email_taken', unchanged: 'unchanged' },
        });
      }
      const saved = (await workerJson<{ email: string }>(res, 'change email')).email;
      try {
        // The Worker changed the address on the server; this session still carries the old one until it is refreshed.
        await db.auth.refreshSession();
      } catch {
        // Offline right after the change: the address shows up at the next refresh. The change itself went through.
      }
      return { email: saved };
    },
    async acceptInvitation(token) {
      const [r] = check(await db.rpc('accept_invitation', { p_token: token }));
      if (!r) throw new ApiError('not_found');
      return { type: r.scope_type as 'org' | 'project' | 'board', id: r.scope_id };
    },
    async sharedWithMe() {
      const rows = check(await db.rpc('shared_with_me'));
      return rows.map(
        (r): SharedItem => ({
          kind: r.kind as SharedItem['kind'],
          id: r.id,
          title: r.title,
          projectName: r.project_name,
          orgName: r.org_name,
          role: r.role as GrantRole,
          updatedAt: r.updated_at,
        }),
      );
    },
    async auditLog(orgId, { before, limit = 50 } = {}) {
      let q = db.from('audit_log').select('id, created_at, actor_id, action, target, meta').eq('org_id', orgId);
      if (before !== undefined) q = q.lt('id', before);
      const rows = check(await q.order('id', { ascending: false }).limit(limit));
      const ids = [...new Set(rows.flatMap((r) => (r.actor_id ? [r.actor_id] : [])))];
      const profiles = ids.length ? check(await db.from('profiles').select('id, display_name').in('id', ids)) : [];
      const names = new Map(profiles.map((p) => [p.id, p.display_name]));
      return rows.map((r) => ({
        id: r.id,
        createdAt: r.created_at,
        actorId: r.actor_id,
        actorName: (r.actor_id && names.get(r.actor_id)) || '',
        action: r.action,
        target: r.target,
        meta: (r.meta ?? {}) as Record<string, unknown>,
      }));
    },

    async listThreads(boardId) {
      const rows = check(
        await db
          .from('comment_threads')
          .select('id, board_id, anchor, resolved_at, resolved_by, created_by, created_at, updated_at, comments(id, author_id, body, deleted_at, created_at)')
          .eq('board_id', boardId)
          .order('updated_at', { ascending: false }),
      );
      const live = (r: (typeof rows)[number]) =>
        r.comments.filter((c) => !c.deleted_at).sort((a, b) => a.created_at.localeCompare(b.created_at));
      const people = await profilesOf(rows.flatMap((r) => live(r).flatMap((c) => (c.author_id ? [c.author_id] : []))));
      return rows.map((r): CommentThread => {
        const messages = live(r);
        const first = messages[0];
        return {
          id: r.id,
          boardId: r.board_id,
          anchor: r.anchor as CommentAnchor,
          resolvedAt: r.resolved_at,
          resolvedBy: r.resolved_by,
          createdBy: r.created_by,
          createdAt: r.created_at,
          updatedAt: r.updated_at,
          count: messages.length,
          first: first ? { authorId: first.author_id, authorName: (first.author_id && people.get(first.author_id)?.name) || '', body: first.body } : null,
          participantIds: [...new Set([r.created_by, ...messages.map((c) => c.author_id)].filter((id): id is string => !!id))],
        };
      });
    },
    async threadMessages(threadId) {
      const rows = check(
        await db
          .from('comments')
          .select('id, thread_id, author_id, body, edited_at, deleted_at, created_at')
          .eq('thread_id', threadId)
          .order('created_at', { ascending: true }),
      );
      const people = await profilesOf(rows.flatMap((r) => (r.author_id ? [r.author_id] : [])));
      return rows.map(
        (r): CommentMessage => ({
          id: r.id,
          threadId: r.thread_id,
          authorId: r.author_id,
          authorName: (r.author_id && people.get(r.author_id)?.name) || '',
          authorColor: (r.author_id && people.get(r.author_id)?.color) || null,
          body: r.body,
          editedAt: r.edited_at,
          deletedAt: r.deleted_at,
          createdAt: r.created_at,
        }),
      );
    },
    async createThread({ boardId, anchor, body }) {
      return check(await db.rpc('create_thread', { p_board: boardId, p_anchor: anchor, p_body: body }));
    },
    async replyComment(threadId, body) {
      return check(await db.rpc('reply_comment', { p_thread: threadId, p_body: body }));
    },
    async editComment(id, body) {
      run(await db.rpc('edit_comment', { p_comment: id, p_body: body }));
    },
    async deleteComment(id) {
      run(await db.rpc('delete_comment', { p_comment: id }));
    },
    async setThreadResolved(id, resolved) {
      run(await db.rpc('set_thread_resolved', { p_thread: id, p_resolved: resolved }));
    },
    async deleteThread(id) {
      run(await db.rpc('delete_thread', { p_thread: id }));
    },
    async mentionables(boardId) {
      return check(await db.rpc('comment_mentionables', { p_board: boardId })).map((r) => ({
        userId: r.user_id,
        displayName: r.display_name,
        color: r.color,
        avatarKey: r.avatar_key,
      }));
    },
    async authors(boardId, userIds) {
      return check(await db.rpc('board_authors', { p_board: boardId, p_users: userIds })).map((r) => ({
        userId: r.user_id,
        displayName: r.display_name,
        color: r.color,
        avatarKey: r.avatar_key,
      }));
    },
    async boardPeople(boardId) {
      return check(await db.rpc('board_people', { p_board: boardId })).map((r) => ({
        userId: r.user_id,
        name: r.display_name,
        color: r.color,
        avatarKey: r.avatar_key,
        role: r.role,
        orgRole: r.org_role === 'owner' || r.org_role === 'admin' || r.org_role === 'member' ? r.org_role : null,
        linkOnly: r.link_only,
        anonymous: r.anonymous,
      }));
    },
    async myNotifications() {
      return check(await db.rpc('my_notifications', {})).map((r) => ({
        id: r.id,
        kind: r.kind === 'reply' ? 'reply' : 'mention',
        boardId: r.board_id,
        boardTitle: r.board_title,
        threadId: r.thread_id,
        authorName: r.author_name,
        excerpt: r.excerpt,
        createdAt: r.created_at,
        readAt: r.read_at,
      }));
    },
    async markNotificationsRead(threadId) {
      return check(await db.rpc('mark_notifications_read', threadId ? { p_thread: threadId } : {}));
    },

    async listVersions(boardId) {
      const rows = check(
        await db
          .from('board_versions')
          .select('id, board_id, kind, label, created_at, created_by, object_count, size_bytes')
          .eq('board_id', boardId)
          .order('created_at', { ascending: false })
          .limit(200),
      );
      const people = await profilesOf(rows.flatMap((r) => (r.created_by ? [r.created_by] : [])));
      return rows.map((r): BoardVersion => ({
        id: r.id,
        boardId: r.board_id,
        kind: r.kind,
        label: r.label,
        createdAt: r.created_at,
        createdBy: r.created_by,
        createdByName: (r.created_by && people.get(r.created_by)?.name) || '',
        objectCount: r.object_count,
        sizeBytes: r.size_bytes,
      }));
    },
    async createVersion(boardId, label) {
      const res = await toWorker(`/api/boards/${encodeURIComponent(boardId)}/versions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await accessToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ label }),
      });
      await versionFailure(res);
      return { id: ((await res.json()) as { id: string }).id };
    },
    async fetchVersion(boardId, versionId) {
      const res = await toWorker(`/api/boards/${encodeURIComponent(boardId)}/versions/${encodeURIComponent(versionId)}`, {
        headers: { Authorization: `Bearer ${await accessToken()}` },
      });
      await versionFailure(res);
      return new Uint8Array(await res.arrayBuffer());
    },
    async restoreVersion(boardId, versionId) {
      const res = await toWorker(`/api/boards/${encodeURIComponent(boardId)}/versions/${encodeURIComponent(versionId)}/restore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await accessToken()}` },
      });
      await versionFailure(res);
    },
    async deleteVersion(versionId) {
      run(await db.rpc('version_delete', { p_version: versionId }));
    },

    async assetHashes(boardId, assetIds) {
      const out = new Map<string, string>();
      for (let i = 0; i < assetIds.length; i += 500) {
        const rows = check(await db.rpc('asset_hashes', { p_board: boardId, p_ids: assetIds.slice(i, i + 500) }));
        for (const r of rows) out.set(r.asset_id, r.sha256);
      }
      return out;
    },
    async seedBoard(boardId, bytes) {
      const res = await toWorker(`/api/boards/${encodeURIComponent(boardId)}/document`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${await accessToken()}`, 'Content-Type': 'application/octet-stream' },
        body: bytes as Uint8Array<ArrayBuffer>,
      });
      if (res.status === 413) throw new ApiError('too_large');
      await versionFailure(res);
    },
    async duplicateBoard(boardId, input) {
      const res = await toWorker(`/api/boards/${encodeURIComponent(boardId)}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await accessToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      await versionFailure(res);
      return { boardId: ((await res.json()) as { boardId: string }).boardId };
    },
    async saveAsTemplate(boardId, input) {
      const res = await toWorker(`/api/boards/${encodeURIComponent(boardId)}/template`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await accessToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      await versionFailure(res);
      return { boardId: ((await res.json()) as { boardId: string }).boardId };
    },
  };
}
